import React, { useEffect, useState, useRef } from 'react';
import { Footer } from '../components/Footer';
import { Header } from '../components/Header';
import { Button } from '../components/Button';
import { Download, ArrowRight, FileText, CheckCircle, BookText } from 'lucide-react';
const PLAYBOOK_PDF_URL = 'https://cdn.nisa.coach/research/Nisa-Context-Engineering-Playbook.pdf';
export const WhitePaper = () => {
  const [showFloatingCTA, setShowFloatingCTA] = useState(true);
  const playbookSectionRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        // Hide button when "Get the free playbook" section is visible
        setShowFloatingCTA(!entry.isIntersecting);
      });
    }, {
      threshold: 0.1,
      rootMargin: '-100px 0px 0px 0px' // Start hiding slightly before section reaches top
    });
    if (playbookSectionRef.current) {
      observer.observe(playbookSectionRef.current);
    }
    return () => {
      if (playbookSectionRef.current) {
        observer.unobserve(playbookSectionRef.current);
      }
    };
  }, []);
  const plays = ['Anchoring AI in evidence-based practice', 'Aligning outputs to real curriculum and lessons', 'Turning frameworks into concrete classroom guidance', 'Teaching AI how expert educators speak and respond', 'Powering authentic simulations and tutoring', 'Generating meaningful practice and feedback at scale'];
  const audience = ['Product leaders and founders making high-stakes decisions about how AI shapes teaching and learning.', 'Designers and learning engineers translating educational values into system behavior and AI outputs.', 'Researchers and educators who want AI tools to reflect what we know works in classrooms.', 'EdTech teams ready to graduate from clever prompts to intentional, scalable AI systems.'];
  const learnings = ['The framework for context engineering in education and why it matters now', 'Why prompt engineering alone fails in educational contexts', 'Proven design patterns you can implement immediately', 'Real-world examples from production EdTech systems', 'How to align AI outputs with research-based teaching practices'];
  const handleScrollToNisa = () => {
    const element = document.getElementById('nisa-connection');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const handleScrollToTop = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  return <div className="flex flex-col min-h-screen bg-white" data-id="element-391">
      {/* SEO Meta Tags */}
      <head data-id="element-392">
        <title data-id="element-393">
          The Context Engineering Playbook for EdTech | Build Trustworthy AI in
          Education
        </title>
        <meta name="description" content="Learn how to design AI systems that understand teaching and learning. Free playbook with practical patterns for building pedagogically-grounded EdTech." data-id="element-394" />
        <meta name="keywords" content="context engineering, AI in education, EdTech, instructional design, AI systems, educational technology, pedagogy" data-id="element-395" />
      </head>

      <Header isStatic={true} hideMobileMenu={true} customNavItems={[]} customActions={[{
      type: 'link',
      label: 'Learn about Nisa',
      href: '#nisa-connection',
      onClick: handleScrollToNisa
    }, {
      type: 'button',
      label: 'Download playbook',
      variant: 'primary',
      href: PLAYBOOK_PDF_URL,
      icon: <Download className="w-5 h-5 mr-2" data-id="element-397" />
    }]} data-id="element-396" />

      <main className="relative flex-grow" data-id="element-398">
        {/* Subtle background elements */}
        <div className="absolute inset-0 bg-gradient-to-b from-cyan-50/40 via-white to-cyan-50/20 -z-10" data-id="element-399" />
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-bl from-cyan-100/15 via-cyan-50/10 to-transparent rounded-full blur-3xl -z-10" data-id="element-400" />

        {/* Floating CTA Button - Mobile Only */}
        <div className={`fixed bottom-0 left-0 right-0 z-50 md:hidden bg-gradient-to-t from-white via-white to-transparent pt-4 pb-6 px-6 flex justify-center transition-all duration-300 ${showFloatingCTA ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}`} data-id="element-401">
          <a href={PLAYBOOK_PDF_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center px-8 py-3 bg-cyan-600 hover:bg-cyan-700 text-white font-medium rounded-lg transition-colors duration-200 shadow-lg" data-id="element-402">
            <Download className="w-6 h-6 mr-2 flex-shrink-0" data-id="element-403" />
            <span className="whitespace-nowrap" data-id="element-404">Download free playbook</span>
          </a>
        </div>

        {/* Hero Section */}
        <section className="w-full pt-16 pb-4 md:pt-24 md:pb-6 lg:pt-32 lg:pb-10 relative" data-id="element-405">
          {/* Mobile Logo - Positioned above eyebrow line */}
          <a href="#" onClick={handleScrollToTop} className="md:hidden absolute top-6 left-6 max-w-[25vw] z-10" data-id="element-406">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jP7ueorRtM9x7giMYpBsys/nisa-capriola-logotype.png" alt="Nisa" className="h-auto w-full" data-id="element-407" />
          </a>

          <div className="container mx-auto px-6 lg:px-16 xl:px-32 2xl:px-48 max-w-[1600px]" data-id="element-408">
            <div className="flex flex-col lg:grid lg:grid-cols-2 gap-4 md:gap-6 lg:gap-10 lg:items-center" data-id="element-409">
              {/* Text content - order-1 on mobile, stays in left column on desktop */}
              <div className="order-1 lg:order-none pt-10 md:pt-0" data-id="element-410">
                <div className="inline-block" data-id="element-411">
                  <span className="text-sm font-semibold text-cyan-600 uppercase tracking-wide" data-id="element-412">
                    Free Resource For Edtech AI Builders
                  </span>
                </div>
                <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl xl:text-5xl 2xl:text-6xl font-normal text-cyan-900 leading-[1.1] tracking-tight mt-2 md:mt-4" data-id="element-413">
                  The Context Engineering Playbook for EdTech
                </h1>
                <p className="text-sm md:text-lg lg:text-xl text-gray-600 font-medium leading-snug md:leading-normal mt-2 md:mt-4" data-id="element-414">
                  A practical guide book for EdTech teams building AI systems,
                  with patterns, examples, and frameworks grounded in real
                  instructional practice
                </p>
              </div>

              {/* Image - order-2 on mobile, stays in right column on desktop */}
              <div className="relative flex items-center justify-center lg:justify-end order-2 lg:order-none lg:row-span-3 md:mx-0" data-id="element-415">
                <img src="https://cdn.magicpatterns.com/uploads/bcUtbBX3GTH27UF94TAiff/nisa-whitepaper.png" alt="Context engineering playbook visualization showing AI system design patterns for education" className="w-full max-w-[288px] mx-auto md:max-w-md lg:max-w-lg md:mx-0" data-id="element-416" />
              </div>

              {/* CTA Button - order-3 on mobile, in left column on desktop - Hidden on mobile, shown on md+ */}
              <div className="hidden md:flex flex-row justify-center items-center order-3 lg:order-none gap-5" data-id="element-417">
                <BookText className="w-12 h-12 text-cyan-600 flex-shrink-0" strokeWidth={1.75} data-id="element-418" />
                <a href={PLAYBOOK_PDF_URL} target="_blank" rel="noopener noreferrer" data-id="element-419">
                  <Button size="md" variant="primary" className="md:!px-6 md:!py-3" data-id="element-420">
                    <Download className="w-6 h-6 md:w-5 md:h-5 mr-2 flex-shrink-0" data-id="element-421" />
                    <span className="whitespace-nowrap" data-id="element-422">
                      <span className="md:hidden" data-id="element-423">Download the playbook</span>
                      <span className="hidden md:inline" data-id="element-424">
                        Download the playbook (PDF)
                      </span>
                    </span>
                  </Button>
                </a>
              </div>

              {/* Trust indicators - order-4 on mobile, in left column on desktop */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 text-sm text-cyan-700 order-4 lg:order-none mb-20 md:mb-0 pl-5" data-id="element-425">
                <div className="flex items-center gap-2" data-id="element-426">
                  <CheckCircle className="w-4 h-4 text-cyan-500 flex-shrink-0" data-id="element-427" />
                  <span data-id="element-428">No registration required</span>
                </div>
                <div className="flex items-center gap-2" data-id="element-429">
                  <CheckCircle className="w-4 h-4 text-cyan-500 flex-shrink-0" data-id="element-430" />
                  <span data-id="element-431">Instant PDF download</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Who This Is For */}
        <section className="w-full py-4 md:py-10" data-id="element-432">
          <div className="container mx-auto px-6 lg:px-16 xl:px-32 2xl:px-48 max-w-[1600px]" data-id="element-433">
            <div className="max-w-5xl" data-id="element-434">
              <h2 className="font-serif text-2xl md:text-3xl font-normal text-cyan-900 mb-3 leading-tight md:leading-normal" data-id="element-435">
                For teams who want to get AI in education right
              </h2>

              <ul className="space-y-2 mb-6" data-id="element-436">
                {audience.map((item, index) => <li key={index} className="flex items-start" data-id="element-437">
                    <span className="text-cyan-500 mr-3 mt-1 flex-shrink-0" data-id="element-438">
                      •
                    </span>
                    <span className="text-base text-cyan-950 leading-normal" data-id="element-439">
                      {item}
                    </span>
                  </li>)}
              </ul>
              <div className="bg-sky-100/60 px-[60px] py-[20px] rounded-lg text-center border-2 border-sky-100" data-id="element-440">
                <p className="text-base text-sky-900 leading-normal font-medium" data-id="element-441">
                  Context engineering is the process of{' '}
                  <span className="font-semibold" data-id="element-442">structuring</span> and{' '}
                  <span className="font-semibold" data-id="element-443">curating</span> the right
                  resources to guide an AI model's responses toward desired
                  outcomes.
                </p>
                <p className="text-base text-sky-900 leading-normal font-medium mt-3" data-id="element-444">
                  Without it, models default to what is most common, not what is
                  most pedagogically sound.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Why This Matters */}
        <section className="w-full py-4 md:py-10 bg-gradient-to-b from-white via-cyan-50/30 to-white" data-id="element-445">
          <div className="container mx-auto px-6 lg:px-16 xl:px-32 2xl:px-48 max-w-[1600px]" data-id="element-446">
            <div className="max-w-5xl" data-id="element-447">
              <h2 className="font-serif text-2xl md:text-3xl font-normal text-cyan-900 mb-6 leading-tight md:leading-normal" data-id="element-448">
                Why context engineering matters now
              </h2>
              <div className="space-y-4 text-base text-cyan-950 leading-normal" data-id="element-449">
                <p data-id="element-450">
                  AI is increasingly shaping how teaching and learning happen.
                  That makes the design choices behind these systems more
                  important than ever. When AI tools lack pedagogical context,
                  they may look impressive but fail to support real classrooms.
                  They risk flattening nuance, amplifying weak practices, or
                  eroding trust with educators.
                </p>
                <p className="font-medium text-cyan-900" data-id="element-451">
                  Context engineering allows teams to encode research,
                  expertise, and instructional intent directly into AI systems,
                  so outputs align with how learning actually works.
                </p>
                <p data-id="element-452">
                  This playbook exists to help EdTech teams design AI that
                  educators can trust, adopt and rely on.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Authorship Strip */}
        <section className="w-full py-8 md:py-10 bg-cyan-50/50" data-id="element-453">
          <div className="container mx-auto px-6 lg:px-16 xl:px-32 2xl:px-48 max-w-[1600px]" data-id="element-454">
            <div className="max-w-5xl" data-id="element-455">
              <h2 className="font-serif text-xl text-cyan-900 mb-6" data-id="element-456">
                About the Authors
              </h2>
              <div className="grid md:grid-cols-2 gap-8 md:gap-10" data-id="element-457">
                <div className="space-y-3" data-id="element-458">
                  <div data-id="element-459">
                    <h3 className="text-lg font-bold text-cyan-900" data-id="element-460">
                      Louisa Rosenheck
                    </h3>
                    <p className="text-sm text-cyan-600 font-medium uppercase tracking-wide mt-1" data-id="element-461">
                      EdTech Design Leader
                    </p>
                  </div>
                  <p className="text-base text-cyan-950 leading-normal" data-id="element-462">
                    Louisa Rosenheck is an EdTech design leader specializing in
                    game-based learning, playful pedagogy, and inclusive
                    learning systems. She has led learning design at Kahoot!,
                    teaches EdTech design at Harvard, and co-authored Resonant
                    Games.
                  </p>
                </div>

                <div className="space-y-3" data-id="element-463">
                  <div data-id="element-464">
                    <h3 className="text-lg font-bold text-cyan-900" data-id="element-465">
                      Agasthya Shenoy
                    </h3>
                    <p className="text-sm text-cyan-600 font-medium uppercase tracking-wide mt-1" data-id="element-466">
                      Educator & Researcher
                    </p>
                  </div>
                  <p className="text-base text-cyan-950 leading-normal" data-id="element-467">
                    Agasthya Shenoy is an educator, researcher, and founder
                    focused on using AI to strengthen teaching and coaching. He
                    founded Nisa at Teaching Lab Studio and brings experience in
                    education research, data science, and instructional
                    practice.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* What You'll Get to Help You Build */}
        <section className="w-full py-4 md:py-10" data-id="element-468">
          <div className="container mx-auto px-6 lg:px-16 xl:px-32 2xl:px-48 max-w-[1600px]" data-id="element-469">
            <div className="max-w-5xl" data-id="element-470">
              <h2 className="font-serif text-2xl md:text-3xl font-normal text-cyan-900 mb-3" data-id="element-471">
                How you'll be equipped to build{' '}
              </h2>
              <p className="text-base text-cyan-700 mb-6" data-id="element-472">
                Practical frameworks and patterns you can implement immediately
              </p>
              <ul className="space-y-2" data-id="element-473">
                {learnings.map((item, index) => <li key={index} className="flex items-start" data-id="element-474">
                    <span className="text-cyan-500 mr-3 mt-1 flex-shrink-0" data-id="element-475">
                      •
                    </span>
                    <span className="text-base text-cyan-950 leading-normal" data-id="element-476">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </div>
          </div>
        </section>

        {/* The Plays */}

        {/* White Paper Preview */}
        <section ref={playbookSectionRef} className="w-full py-4 md:py-10" data-id="element-477">
          <div className="container mx-auto px-6 lg:px-16 xl:px-32 2xl:px-48 max-w-[1600px]" data-id="element-478">
            <div className="bg-gradient-to-br from-sky-50/60 to-sky-50/40 rounded-2xl p-6 md:p-8 border border-sky-200" data-id="element-479">
              <div className="grid md:grid-cols-2 gap-6 items-center" data-id="element-480">
                <div data-id="element-481">
                  <h2 className="font-serif text-2xl md:text-3xl font-normal text-sky-900 mb-3" data-id="element-482">
                    Get the free playbook
                  </h2>
                  <p className="text-base text-sky-950 leading-normal mb-4" data-id="element-483">
                    A practical guide to building AI systems that understand
                    teaching and learning, with patterns, examples, and
                    evidence-based approaches you can apply.
                  </p>
                  <ul className="space-y-2 mb-5" data-id="element-484">
                    <li className="flex items-center text-sky-950 text-sm" data-id="element-485">
                      <CheckCircle className="w-4 h-4 text-sky-500 mr-2 flex-shrink-0" data-id="element-486" />
                      <span data-id="element-487">
                        A grounded approach to AI in education, rooted in
                        evidence-based practice.
                      </span>
                    </li>
                    <li className="flex items-center text-sky-950 text-sm" data-id="element-488">
                      <CheckCircle className="w-4 h-4 text-sky-500 mr-2 flex-shrink-0" data-id="element-489" />
                      <span data-id="element-490">
                        Context engineering patterns for pedagogy, curriculum
                        alignment, tone, and feedback.
                      </span>
                    </li>
                    <li className="flex items-center text-sky-950 text-sm" data-id="element-491">
                      <CheckCircle className="w-4 h-4 text-sky-500 mr-2 flex-shrink-0" data-id="element-492" />
                      <span data-id="element-493">
                        A clear system model for structuring AI knowledge and
                        outputs.
                      </span>
                    </li>
                    <li className="flex items-center text-sky-950 text-sm" data-id="element-494">
                      <CheckCircle className="w-4 h-4 text-sky-500 mr-2 flex-shrink-0" data-id="element-495" />
                      <span data-id="element-496">
                        Lightweight tools for planning and iteration as your
                        product evolves.
                      </span>
                    </li>
                  </ul>
                  <a href={PLAYBOOK_PDF_URL} target="_blank" rel="noopener noreferrer" data-id="element-497">
                    <Button size="lg" variant="primary" data-id="element-498">
                      <Download className="w-5 h-5 mr-2" data-id="element-499" />
                      Download the playbook (PDF)
                    </Button>
                  </a>
                  <p className="text-sm text-sky-700 mt-2 text-center pr-5" data-id="element-500">
                    Free • No email required • Instant access
                  </p>
                </div>
                <div className="relative" data-id="element-501">
                  <img src="https://cdn.magicpatterns.com/uploads/1szKvBLFhd6LjFRKjk61sJ/NisaPlaybook_Cover.png" alt="The Context Engineering Playbook for EdTech cover" className="h-full w-full rounded-lg shadow-lg object-cover ring-1 ring-inset ring-cyan-100" data-id="element-502" />
                  <div className="absolute -bottom-4 -right-4 aspect-[3/4] w-3/4 bg-sky-50/80 rounded-lg shadow-lg border border-sky-200 -z-10" data-id="element-503" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Connection to Nisa */}
        <section id="nisa-connection" className="w-full py-4 md:py-10 pb-10 bg-gradient-to-b from-white via-cyan-50/25 to-white" data-id="element-504">
          <div className="container mx-auto px-6 lg:px-16 xl:px-32 2xl:px-48 max-w-[1600px]" data-id="element-505">
            <div className="max-w-5xl" data-id="element-506">
              <h3 className="font-serif text-2xl md:text-3xl font-normal text-cyan-900 mb-4 leading-tight md:leading-normal" data-id="element-507">
                See context engineering in action with Nisa
              </h3>
              <div className="space-y-4 mb-6" data-id="element-508">
                <p className="text-base text-cyan-950 leading-normal" data-id="element-509">
                  This playbook reflects how Nisa builds AI in practice. Nisa is
                  a thoughtful AI ecosystem for instructional coaching, designed
                  to carry context forward across every interaction. Nisa keeps
                  educator judgment at the center while making coaching more
                  sustainable and impactful.
                </p>
              </div>
              <a href="https://nisa.coach" data-id="element-510">
                <Button size="lg" variant="secondary" data-id="element-511">
                  Explore Nisa
                  <ArrowRight className="w-5 h-5 ml-2" data-id="element-512" />
                </Button>
              </a>
            </div>
          </div>
        </section>

        {/* Footer Callout */}
      </main>
      <Footer data-id="element-513" />
    </div>;
};